import {
  ChoiceGroup,
  DatePicker,
  DayOfWeek,
  defaultDatePickerStrings,
  DirectionalHint,
  IChoiceGroupOption,
  IconButton,
  Label,
  Text,
  TextField,
  TooltipDelay,
  TooltipHost,
} from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import * as React from 'react';
import { FormEvent, useContext, useState } from 'react';

import { AttachmentItem, DeleteAttachmentRequest } from '../../../../Common/CommonModels/Attachments';
import { AttachmentsForm } from '../../../../Common/Components/AttachmentsForm';
import {
  onRenderLabelPrivacyApprovalArtifacts,
  onRenderLabelPrivacyApprovalDate,
} from '../../../../Common/Components/Labels';
import { DataOwnerRequestContext } from '../../../../Common/DataOwnerModels/DataOwnerRequestDetails';
import * as styled from '../DataOwnerManagement.styled';

export function RequestStep(): React.ReactElement {
  // Data Owner Request Context
  const { dataOwnerRequestsList, dataOwnerRequestDetails } = useContext(DataOwnerRequestContext);

  // Tooltip State
  const requestNameTooltipId = useId('ruleNameTooltip');

  // Request Step Errors
  const [nameError, setNameError] = useState<boolean>(false);
  const [descriptionError, setDescriptionError] = useState<boolean>(false);
  const [additionalNotesError, setAdditionalNotesError] = useState<boolean>(false);

  const environmentOptions: IChoiceGroupOption[] = [
    {
      key: 'NonProd',
      text: 'Non-Production',
      styles: {
        root: {
          margin: '0px 32px 0px 0px',
        },
      },
    },
    {
      key: 'Prod',
      text: 'Production',
      styles: {
        root: {
          margin: '0px 0px 0px 0px',
        },
      },
    },
  ];

  // Informational Icons
  const onRenderLabelName = () => {
    return (
      <styled.LabelContainer>
        <Label>Request Name</Label>
        <styled.CustomRequiredLabel>*</styled.CustomRequiredLabel>
        <TooltipHost
          id={requestNameTooltipId}
          directionalHint={DirectionalHint.rightCenter}
          closeDelay={TooltipDelay.zero}
          styles={styled.tooltipHostStyles}
          tooltipProps={{
            onRenderContent: () => {
              return (
                <styled.CustomTooltipContainer>
                  <Text>This field holds Custom Request Name, used for easy of identification.</Text>
                </styled.CustomTooltipContainer>
              );
            },
          }}
        >
          <IconButton
            iconProps={{ iconName: 'Info' }}
            aria-describedby={requestNameTooltipId}
            styles={{ root: { marginBottom: -3 } }}
          />
        </TooltipHost>
      </styled.LabelContainer>
    );
  };

  const onRenderEnvironmentField = () => {
    return (
      <div>
        <styled.LabelContainer>
          <Label>Environment</Label>
          <styled.CustomRequiredLabel>*</styled.CustomRequiredLabel>
          <TooltipHost
            id={requestNameTooltipId}
            directionalHint={DirectionalHint.rightCenter}
            closeDelay={TooltipDelay.zero}
            styles={styled.tooltipHostStyles}
            tooltipProps={{
              onRenderContent: () => {
                return (
                  <styled.CustomTooltipContainer>
                    <Text>
                      The request is created against the selected Environment. <br />
                      The environment cannot be changed once the entities or rulesets are included in the request. To
                      alter the environment, you can remove the added entities and rulesets
                    </Text>
                  </styled.CustomTooltipContainer>
                );
              },
            }}
          >
            <IconButton
              iconProps={{ iconName: 'Info' }}
              aria-describedby={requestNameTooltipId}
              styles={{ root: { marginBottom: -3 } }}
            />
          </TooltipHost>
        </styled.LabelContainer>
        <ChoiceGroup
          data-testid="data-owner-request-environment"
          id="environment"
          placeholder="Select Environment"
          selectedKey={dataOwnerRequestDetails?.request?.environment === 1 ? 'Prod' : 'NonProd'}
          onChange={onChangeEnvironment}
          options={environmentOptions}
          required={true}
          disabled={dataOwnerRequestDetails.cdms?.length > 0 || dataOwnerRequestDetails.ruleSets?.length > 0}
          styles={{ flexContainer: { display: 'flex' } }}
        />
      </div>
    );
  };

  const onChangeName = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    dataOwnerRequestDetails.addUpdateRequest({
      ...dataOwnerRequestDetails.request,
      name: newValue,
    });
  };

  const onChangeDescription = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    dataOwnerRequestDetails.addUpdateRequest({
      ...dataOwnerRequestDetails.request,
      description: newValue,
    });
  };

  const onChangeEnvironment = (event: React.FormEvent<HTMLElement>, option: IChoiceGroupOption) => {
    dataOwnerRequestDetails.addUpdateRequest({
      ...dataOwnerRequestDetails.request,
      environment: option.key === 'Prod' ? 1 : 0,
    });
  };

  const onChangeAttachmentList = (newAttachmentList: AttachmentItem[]) => {
    dataOwnerRequestDetails.updateAttachmentList(newAttachmentList);
  };

  const onChangeDeleteAttachmentRequestList = (newDeleteAttachmentRequestList: DeleteAttachmentRequest[]) => {
    dataOwnerRequestDetails.updateDeleteAttachmentRequestList(newDeleteAttachmentRequestList);
  };

  const onChangePrivacyApprovalDate = (date: Date | null | undefined) => {
    if (date) {
      dataOwnerRequestDetails.addUpdateRequest({
        ...dataOwnerRequestDetails.request,
        privacyApprovalDate: date.toISOString(),
      });
    }
  };

  const onChangeAdditionalNotes = (event: FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    dataOwnerRequestDetails.addUpdateRequest({
      ...dataOwnerRequestDetails.request,
      additionalNotes: newValue,
    });
  };

  const getNameErrorMessage = (newValue?: string) => {
    const isDuplicate = dataOwnerRequestsList?.some(
      (request) => request.name?.toLowerCase().trim() === newValue.toLowerCase().trim()
    );
    if (isDuplicate) {
      setNameError(true);
      return 'Request Name already exists.';
    }
    if (!newValue) {
      setNameError(true);
      return 'Please enter a valid name with printable characters only.';
    } else if (/^[\x00-\x7F]*$/.test(newValue)) {
      setNameError(false);
      return '';
    } else {
      setNameError(true);
      return 'Please enter a valid name with printable characters only.';
    }
  };

  const getDescriptionErrorMessage = (newValue?: string) => {
    if (/^[\x00-\x7F]*$/.test(newValue)) {
      setDescriptionError(false);
      return '';
    } else {
      setDescriptionError(true);
      return 'Please enter a valid description with printable characters only.';
    }
  };

  const getAdditionalNotesErrorMessage = (newValue?: string) => {
    if (/^[\x00-\x7F]*$/.test(newValue) || !newValue) {
      setAdditionalNotesError(false);
      return '';
    } else {
      setAdditionalNotesError(true);
      return 'Please enter valid notes/comments with printable characters only.';
    }
  };

  return (
    <styled.RequestStepContainer>
      {onRenderEnvironmentField()}
      <TextField
        data-testid="data-owner-request-name"
        onRenderLabel={onRenderLabelName}
        id="requestName"
        placeholder="Enter Custom Request Name"
        value={dataOwnerRequestDetails?.request?.name}
        onChange={onChangeName}
        onGetErrorMessage={getNameErrorMessage}
        validateOnLoad={false}
        validateOnFocusOut={true}
      />

      <TextField
        data-testid="data-owner-request-description"
        label="Description"
        id="description"
        placeholder="Enter Custom Description"
        value={dataOwnerRequestDetails?.request?.description}
        onChange={onChangeDescription}
        onGetErrorMessage={getDescriptionErrorMessage}
        validateOnLoad={false}
        validateOnFocusOut={true}
        multiline={true}
        autoAdjustHeight={true}
      />

      {onRenderLabelPrivacyApprovalArtifacts()}
      <AttachmentsForm
        requestId={dataOwnerRequestDetails.request.id}
        attachmentList={dataOwnerRequestDetails.attachmentList}
        setAttachmentList={onChangeAttachmentList}
        deleteAttachmentRequestList={dataOwnerRequestDetails.deleteAttachmentRequestList}
        setDeleteAttachmentRequestList={onChangeDeleteAttachmentRequestList}
      />

      {onRenderLabelPrivacyApprovalDate()}
      <DatePicker
        data-testid="data-owner-request-privacy-approval-date"
        id="privacyApprovalDate"
        placeholder="Select a date..."
        ariaLabel="Privacy Approval Date"
        value={new Date(dataOwnerRequestDetails.request.privacyApprovalDate)}
        onSelectDate={onChangePrivacyApprovalDate}
        maxDate={new Date()}
        firstDayOfWeek={DayOfWeek.Sunday}
        firstWeekOfYear={1}
        showMonthPickerAsOverlay={true}
        showGoToToday={false}
        strings={defaultDatePickerStrings}
      />

      <TextField
        data-testid="data-owner-request-additional-notes"
        label="Additional Notes"
        id="additionalNotes"
        placeholder="Enter Additional Notes"
        value={dataOwnerRequestDetails.request.additionalNotes}
        onChange={onChangeAdditionalNotes}
        onGetErrorMessage={getAdditionalNotesErrorMessage}
        required={false}
        validateOnLoad={false}
        validateOnFocusOut={true}
        multiline={true}
        autoAdjustHeight={true}
        rows={3}
      />
    </styled.RequestStepContainer>
  );
}
